import { type MouseEvent } from 'react';
import { Link } from 'react-router';

import { AdminUrls } from '@treneo/features/admin';
import { NotificationBell } from '@treneo/features/notifications';
import { useTrainerInvitationNotifications } from '@treneo/features/organizations';
import { urls } from '@treneo/shared/constants';
import { useAppTranslations } from '@treneo/shared/i18n';
import { useAppLocation, useAppNavigate, useAppSearchParams } from '@treneo/shared/router';
import { Colors } from '@treneo/shared/themes';
import {
  LanguageBar,
  Logo,
  LOGO_COLORS,
  LOGO_VARIANTS,
  styled,
  Typography
} from '@treneo/shared/ui';

import { BaseNavbar } from '../navbar/base-navbar';
import { UserProfileModal } from '../user-profile-modal/user-profile-modal';

import { AdminBreadcrumbs } from './admin-breadcrumbs';
import { buildTrainingsPath } from './trainings-path';

const BreadcrumbSeparator = styled(Typography)`
  color: ${Colors.textColor};
  opacity: 0.5;
  user-select: none;
`;

const TrainingsLink = styled(Link)`
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: none;
    color: inherit;
  }
`;

type AdminNavbarProps = {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
};

export const AdminNavbar = (_props: Readonly<AdminNavbarProps>) => {
  const { t } = useAppTranslations();
  const location = useAppLocation();
  const navigate = useAppNavigate();
  const showBreadcrumbs = location.pathname !== AdminUrls.adminPanel;
  const { notifications, isLoading: areNotificationsLoading } =
    useTrainerInvitationNotifications(true);

  const hasNotifications = notifications.length > 0;
  const shouldShowNotificationBell = hasNotifications || areNotificationsLoading;

  const [searchParams] = useAppSearchParams();
  const trainingsPath = buildTrainingsPath(searchParams);

  const handleLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate(urls.homePage);
  };

  const leftContent = (
    <>
      <Link to={urls.homePage} onClick={handleLogoClick}>
        <Logo variant={LOGO_VARIANTS.menu} color={LOGO_COLORS.dark} />
      </Link>
      <TrainingsLink to={trainingsPath}>
        <Typography variant='body2'>{t('coursesPage.courses')}</Typography>
      </TrainingsLink>
      {showBreadcrumbs && <BreadcrumbSeparator variant='body2'>|</BreadcrumbSeparator>}
      <AdminBreadcrumbs />
    </>
  );

  const rightContent = (
    <>
      {shouldShowNotificationBell && (
        <NotificationBell notifications={notifications} isLoading={areNotificationsLoading} />
      )}
      <UserProfileModal />
      <LanguageBar />
    </>
  );

  return (
    <BaseNavbar
      leftContent={leftContent}
      centerContent={null}
      rightContent={rightContent}
      variant='admin'
    />
  );
};
